import styled, { css } from "styled-components";

import { colors, fonts, fontSizes, radius, transitions } from "@/styles/design-system";

/* ── Section Heading ── */

export const SectionHeading = styled.h2`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${colors.text.primary};
  margin: 40px 0 16px 0;
  padding: 0 0 12px 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: inline-block;
    width: 16px;
    height: 16px;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' fill='%23ffffff' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M23.498 6.186a2.994 2.994 0 0 0-2.112-2.12C19.544 3.5 12 3.5 12 3.5s-7.544 0-9.386.566A2.994 2.994 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a2.994 2.994 0 0 0 2.112 2.12C4.456 20.5 12 20.5 12 20.5s7.544 0 9.386-.566a2.994 2.994 0 0 0 2.112-2.12C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    flex-shrink: 0;
    position: relative;
    top: -1px;
  }

  &:first-of-type {
    margin-top: 0;
  }
`;

/* ── Toolbar ── */

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 0 16px 0 40px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 8px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: #fbf4ec;
  outline: none;
  transition: border-color ${transitions.fast};
  height: 46px;

  &::placeholder {
    color: rgba(251, 244, 236, 0.5);
  }

  &:focus {
    border-color: #ba905c;
  }
`;

export const SearchWrap = styled.div`
  position: relative;
  flex: 1;

  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    stroke: rgba(251, 244, 236, 0.5);
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    pointer-events: none;
  }
`;

export const SortWrapper = styled.div`
  flex-shrink: 0;

  .MuiInputBase-root {
    min-width: 0 !important;
    width: auto !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid rgba(186, 144, 92, 0.2) !important;
    border-radius: 8px !important;
    height: 46px !important;
    cursor: pointer !important;
  }

  .MuiSelect-select {
    background: transparent !important;
    border: none !important;
    color: #e0d2c1 !important;
    font-family: "Raleway", sans-serif !important;
    font-size: 0.875rem !important;
    letter-spacing: 0.02em !important;
    padding: 0 32px 0 14px !important;
    min-height: 0 !important;
    width: auto !important;
    transform: translateY(1px);
  }

  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }

  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }

  .Mui-focused.MuiInputBase-root,
  .MuiInputBase-root.Mui-focused {
    border-color: rgba(186, 144, 92, 0.2) !important;
    box-shadow: none !important;
  }

  .MuiSelect-icon {
    display: none !important;
  }

  .MuiSelect-select::after {
    content: "";
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 10px;
    height: 6px;
    background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%23AC8D75' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
  }

  > * {
    min-width: 0 !important;
    width: auto !important;
  }
`;

/* ── License Card ── */

export const LicenseCard = styled.div`
  background: rgba(81, 58, 40, 0.35);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(235, 194, 142, 0.1);
  border-radius: ${radius["2xl"]};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const CardCover = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.md};
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(75, 55, 39, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const PlayOverlay = styled.div<{ $playing?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: 1;
  transition: opacity ${transitions.fast};

  svg {
    width: 20px;
    height: 20px;
    fill: #fff;
    stroke: none;
  }
`;

export const CardTrackInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CardTrackTitle = styled.div`
  font-family: ${fonts.heading};
  font-size: 1.125rem;
  font-weight: 500;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CardTrackArtist = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.muted};
  margin-top: 2px;
`;

export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 12px;
  margin-top: -4px;
  border-top: 1px solid rgba(235, 194, 142, 0.06);
`;

export const ActionBtn = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: ${radius.md};

  /* When the claim-card action row stacks full-width on phone/tablet, centre
     the icon + label instead of leaving them left-aligned. */
  @media (max-width: 1149.98px) {
    justify-content: center;
  }
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  cursor: pointer;
  transition: all ${transitions.fast};

  ${({ $primary }) =>
    $primary
      ? css`
          background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
          border: none;
          color: ${colors.brown.darkest};
          /* Only the enabled (clickable) button lifts — the disabled
             "Moving to…" / "Submitted!" pills must not move on hover. */
          &:hover:not(:disabled) { transform: translateY(-1px); }
        `
      : css`
          background: transparent;
          border: 1px solid rgba(235, 194, 142, 0.2);
          color: ${colors.text.link};
          &:hover {
            background: rgba(235, 194, 142, 0.06);
            border-color: rgba(235, 194, 142, 0.4);
          }
        `}

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    /* Blue on every card button, gold or ghost. The ghost ones used to
       inherit the button's own gold colour, which made the icon and the
       label the same weight — the icon then read as decoration rather than as
       the thing naming the action. Colour-overridden buttons (green resolve,
       blue connect) still re-set their stroke inline.

       Except the destructive one. Remove is the only action on a card that
       cannot be undone, and it is the only one that should look different
       before it is read. */
    stroke: ${({ $danger }) => ($danger ? colors.accent.red : "#60a3d7")};
  }
`;

/* ── Video Info ── */

export const VideoInfo = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.body};
  text-decoration: none;
  transition: color ${transitions.fast};

  &:hover {
    color: ${colors.text.primary};
  }
`;

export const VideoThumb = styled.div`
  width: 160px;
  aspect-ratio: 16 / 9;
  border-radius: ${radius.md};
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(75, 55, 39, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;

  /* Shrink on phone/tablet so the title + meta keep usable width next to it. */
  @media (max-width: 1149.98px) {
    width: 128px;
  }
  @media (max-width: 600px) {
    width: 104px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: rgba(235, 194, 142, 0.25);
  }
`;

export const VideoDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

export const VideoTitle = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.body};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const VideoUrl = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  color: ${colors.text.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ── Credit Text ── */

export const CreditTextSlide = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;

  > * {
    overflow: hidden;
  }
`;

export const CreditTextBox = styled.div`
  background: rgba(255, 251, 247, 0.04);
  border: 1px solid rgba(235, 194, 142, 0.08);
  border-radius: ${radius.md};
  padding: 16px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  line-height: 1.8;
  color: ${colors.text.body};
  white-space: pre-line;
  /* Long unbreakable lines in the credit block (e.g. the Free Use URL) must
     wrap — otherwise the box's min-content width stretches the whole claim
     card past its right edge, even while the panel is collapsed. */
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
  margin-top: -8px;
  /* Not selectable/copyable on any device — users copy via the Copy button.
     -webkit-touch-callout disables iOS's tap-and-hold copy menu on touch. */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  /* Smaller credit text on phone + tablet so the block reads comfortably in
     the narrower card; desktop (≥1024) keeps the standard body size. */
  @media (max-width: 1023px) {
    font-size: ${fontSizes.sm};
  }
`;

export const CreditCopyBtn = styled.button<{ $copied?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  margin-top: 12px;
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all ${transitions.fast};
  /* Blue copy button — matches the demo + the site's blue accent. */
  background: ${({ $copied }) => ($copied ? "rgba(52, 211, 153, 0.15)" : "rgba(156, 211, 253, 0.12)")};
  border: 1px solid ${({ $copied }) => ($copied ? "rgba(52, 211, 153, 0.3)" : "rgba(156, 211, 253, 0.3)")};
  color: ${({ $copied }) => ($copied ? colors.accent.green : colors.accent.blue)};

  &:hover {
    background: ${({ $copied }) => ($copied ? "rgba(52, 211, 153, 0.2)" : "rgba(156, 211, 253, 0.2)")};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* ── Compact unconnected license card ── */

export const CompactCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius["2xl"]};
  padding: 16px 24px;
  margin-bottom: 12px;

  @media (max-width: 640px) {
    padding: 16px 20px;
  }
`;

export const CompactCover = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.md};
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(75, 55, 39, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const CompactInfo = styled.div`
  min-width: 0;
  flex-shrink: 0;
  width: 180px;

  @media (max-width: 640px) {
    width: auto;
    flex: 1;
  }
`;

export const CompactTitle = styled.div`
  font-family: ${fonts.heading};
  font-size: 1.125rem;
  font-weight: 500;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const CompactArtist = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.muted};
  margin-top: 2px;
`;

export const CompactUrlInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: #fbf4ec;
  outline: none;
  transition: border-color ${transitions.fast};
  height: 38px;

  &::placeholder {
    color: rgba(251, 244, 236, 0.4);
  }

  &:focus {
    border-color: #ba905c;
  }
`;

export const CompactConnectBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%);
  border: none;
  border-radius: ${radius.md};
  color: ${colors.brown.darkest};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  cursor: pointer;
  transition: all ${transitions.fast};
  flex-shrink: 0;
  height: 38px;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const CompactRemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${radius.md};
  background: transparent;
  border: 1px solid rgba(235, 194, 142, 0.15);
  cursor: pointer;
  transition: all ${transitions.fast};
  flex-shrink: 0;
  color: ${colors.text.muted};

  &:hover {
    background: rgba(228, 122, 106, 0.1);
    border-color: rgba(228, 122, 106, 0.3);
    color: ${colors.accent.red};
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

/* ── Blue Action Button ── */

export const BlueActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: ${radius.md};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  cursor: pointer;
  transition: all ${transitions.fast};
  background: rgba(156, 211, 253, 0.12);
  border: 1px solid rgba(156, 211, 253, 0.25);
  color: ${colors.accent.blue};

  &:hover {
    background: rgba(156, 211, 253, 0.2);
    border-color: rgba(156, 211, 253, 0.4);
    transform: translateY(-1px);
  }
`;

/* ── Channel Toggle ── */

export const ChannelToggle = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 14px 20px;
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius["2xl"]};
  cursor: pointer;
  transition: all ${transitions.fast};
  margin-bottom: 16px;

  &:hover {
    border-color: ${colors.border.default};
  }

  svg.yt-icon {
    width: 20px;
    height: 20px;
    fill: #ff0000;
    flex-shrink: 0;
  }

  svg.chevron {
    width: 14px;
    height: 14px;
    stroke: ${colors.text.muted};
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    margin-left: auto;
    transition: transform ${transitions.fast};
    transform: ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
  }
`;

export const ChannelToggleText = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 400;
  color: ${colors.text.body};
`;

export const ChannelToggleStatus = styled.span<{ $connected: boolean }>`
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  font-weight: 500;
  color: ${({ $connected }) => ($connected ? colors.accent.green : colors.text.muted)};
`;

/* ── Claim Card ── */

export const ClaimCard = styled.div`
  background: rgba(156, 211, 253, 0.04);
  border: 1px solid rgba(156, 211, 253, 0.15);
  border-radius: ${radius["2xl"]};
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ClaimContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ClaimTitle = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 400;
  color: ${colors.text.primary};
  line-height: 1.4;
`;

export const ClaimMeta = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.muted};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const ClaimVideoLink = styled.a`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.accent.blue};
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;

  &:hover {
    color: ${colors.accent.blueLight};
  }
`;

export const ClaimActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const ClaimDismissBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: ${radius.md};
  background: transparent;
  border: 1px solid rgba(235, 194, 142, 0.15);
  cursor: pointer;
  transition: all ${transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.muted};

  &:hover {
    background: rgba(228, 122, 106, 0.1);
    border-color: rgba(228, 122, 106, 0.3);
    color: ${colors.accent.red};
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

/* ── Upgrade Hint ── */

export const UpgradeHint = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  font-weight: 400;
  color: ${colors.text.muted};
  text-decoration: none;
  transition: color ${transitions.fast};

  &:hover {
    color: ${colors.accent.blue};
  }
`;

/* ── Status Message ── */

export const StatusMessage = styled.div<{ $status: string }>`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 400;
  line-height: 1.6;
  padding: 12px 16px;
  border-radius: ${radius.md};
  margin-top: -4px;

  ${({ $status }) => {
    const s = $status.toLowerCase();
    if (s === "music use") return `background: rgba(228, 122, 106, 0.08); border: 1px solid rgba(228, 122, 106, 0.15); color: #f38373;`;
    if (s.includes("credit text")) return `background: rgba(255, 215, 0, 0.06); border: 1px solid rgba(255, 215, 0, 0.15); color: #ffd700;`;
    return `background: rgba(251, 244, 236, 0.04); border: 1px solid rgba(251, 244, 236, 0.08); color: #fff;`;
  }}
`;

/* ── Free User Hint Box ── */

export const FreeHintBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  background: rgba(186, 144, 92, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: ${radius.lg};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.gold.text};
  line-height: 1.5;
  margin-bottom: 8px;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    stroke: ${colors.gold.primary};
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

/* ── Progress Bar (for subscription credits) ── */

export const CreditsBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

export const CreditsLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.body};
`;

export const CreditsCount = styled.span`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  color: ${colors.accent.blue};
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(90deg, ${colors.accent.green}, ${colors.accent.greenDark});
  border-radius: 3px;
  transition: width 0.4s ease;
`;

/* ── Auto-approve Toggle ── */

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius["2xl"]};
  margin-bottom: 16px;
`;

export const ToggleLabel = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ToggleName = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 400;
  color: ${colors.text.primary};
`;

export const ToggleDesc = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.muted};
  margin-top: 2px;
  line-height: 1.4;
`;

export const ToggleSwitch = styled.button<{ $on: boolean }>`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: background ${transitions.fast};
  background: ${({ $on }) => ($on ? colors.accent.blue : "rgba(255,255,255,0.12)")};

  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? "23px" : "3px")};
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: left ${transitions.fast};
  }
`;

/* ── Creator Submission Card ── */

export const SubmissionCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  background: ${colors.surface.card};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius["2xl"]};
  padding: 24px;
  margin-bottom: 16px;

  @media (max-width: 640px) {
    padding: 16px 20px;
  }
`;

export const SubmissionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SubmissionTitle = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 400;
  color: ${colors.text.primary};
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

export const SubmissionMeta = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.muted};
  margin-top: 2px;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

export const SubmissionStatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: 500;
  flex-shrink: 0;

  ${({ $status }) => {
    const s = $status.toLowerCase();
    if (s === "split" || s === "whitelisted") return `background: rgba(52, 211, 153, 0.12); color: ${colors.accent.green}; border: 1px solid rgba(52, 211, 153, 0.25);`;
    if (s === "ready to release") return `background: rgba(156, 211, 253, 0.1); color: ${colors.accent.blue}; border: 1px solid rgba(156, 211, 253, 0.25);`;
    if (s === "reviewing" || s === "pending" || s === "analyzing" || s === "searching") return `background: rgba(251, 244, 236, 0.06); color: ${colors.text.muted}; border: 1px solid rgba(251, 244, 236, 0.1);`;
    if (s === "credit text" || s === "timestamps" || s === "credit text & timestamps") return `background: rgba(245, 166, 35, 0.1); color: #f5a623; border: 1px solid rgba(245, 166, 35, 0.3);`;
    if (s === "private video" || s === "no claim detected") return `background: rgba(245, 166, 35, 0.1); color: #f5a623; border: 1px solid rgba(245, 166, 35, 0.3);`;
    if (s === "music use" || s === "not covered" || s === "credit missing" || s === "void") return `background: rgba(228, 122, 106, 0.1); color: ${colors.accent.red}; border: 1px solid rgba(228, 122, 106, 0.25);`;
    if (s === "paused") return `background: rgba(251, 244, 236, 0.06); color: ${colors.text.muted}; border: 1px solid rgba(251, 244, 236, 0.15);`;
    return `background: rgba(251, 244, 236, 0.06); color: ${colors.text.muted}; border: 1px solid rgba(251, 244, 236, 0.1);`;
  }}
`;

/* ── Creator Tabs ── */

export const CreatorTabContainer = styled.div`
  display: inline-flex;
  gap: 6px;
  padding: 5px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(186, 144, 92, 0.22);
  border-radius: 12px;
  margin-bottom: 24px;
  align-self: flex-start;
  backdrop-filter: blur(8px);
`;

export const CreatorTab = styled.button<{ $active: boolean }>`
  height: 46px;
  padding: 0 28px;
  border-radius: 9px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.lg};
  font-weight: 500;
  letter-spacing: 0.2px;
  color: ${({ $active }) => ($active ? colors.text.primary : "rgba(251, 244, 236, 0.6)")};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(255, 220, 168, 0.18), rgba(255, 220, 168, 0.08))"
      : "transparent"};
  border: 1px solid ${({ $active }) => ($active ? "rgba(235, 194, 142, 0.35)" : "transparent")};
  box-shadow: ${({ $active }) =>
    $active ? "0 0 18px rgba(186, 144, 92, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.08)" : "none"};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 10px;

  &:hover {
    color: ${colors.text.primary};
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(135deg, rgba(255, 220, 168, 0.22), rgba(255, 220, 168, 0.1))"
        : "rgba(255, 255, 255, 0.05)"};
  }
`;

export const CreatorTabIcon = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${({ $active }) => ($active ? colors.accent.blue : "inherit")};

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

/* ── Subscription Claim Status Badge ── */

export const SubClaimBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 500;
  flex-shrink: 0;

  ${({ $status }) => {
    const s = $status.toLowerCase();
    if (s === "resolved") return `background: rgba(52, 211, 153, 0.12); color: ${colors.accent.green}; border: 1px solid rgba(52, 211, 153, 0.25);`;
    return `background: rgba(156, 211, 253, 0.12); color: ${colors.accent.blue}; border: 1px solid rgba(156, 211, 253, 0.25);`;
  }}
`;

/* ── Error Footer (Error ID + envelope) ── */

export const ErrorFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding-top: 14px;
  margin-top: 14px;
  border-top: 1px solid rgba(235, 194, 142, 0.06);
`;

export const ErrorIdLabel = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(235, 194, 142, 0.08);
  font-family: ${fonts.number};
  font-size: ${fontSizes.xs};
  font-weight: 500;
  color: rgba(251, 244, 236, 0.3);
  cursor: pointer;
  transition: all ${transitions.fast};
  letter-spacing: 0.5px;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(235, 194, 142, 0.15);
    color: ${colors.text.muted};
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const EnvelopeLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(235, 194, 142, 0.08);
  color: rgba(251, 244, 236, 0.3);
  cursor: pointer;
  transition: all ${transitions.fast};
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(235, 194, 142, 0.15);
    color: ${colors.text.muted};
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.5;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

export const UpgradeCta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border-radius: 8px;
  background: rgba(156, 211, 253, 0.1);
  border: 1px solid rgba(156, 211, 253, 0.25);
  color: ${colors.accent.blue};
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all ${transitions.fast};

  &:hover {
    background: rgba(156, 211, 253, 0.18);
    border-color: rgba(156, 211, 253, 0.4);
  }

  svg {
    width: 14px;
    height: 14px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;
